import { AspectRatio } from "@chakra-ui/react";

import { useCallback, useContext } from "react";

import { Skeleton } from "@chakra-ui/react";

import { Box, Portal, useDisclosure } from "../../internal/exports";
import { Dialog } from "../../chakra-slots";

import { ImageProps, Image as StorybookImage } from "../../image/image";
import { Header } from "../header/header";
import { ContributionContext } from "../context";

// Padding is set per-slot below, so flush the recipe's.
const flushDialogSlotPadding = {
  p: 0,
  px: 0,
  py: 0,
  pt: 0,
  pb: 0,
  pl: 0,
  pr: 0
};

export function Image() {
  const { variant, imageSrc, imageAlt, isLoaded, showImage, onClick } =
    useContext(ContributionContext);
  const { open, onOpen, onClose } = useDisclosure();

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        onClose();
      }
    },
    [onClose]
  );

  if (!showImage || !imageSrc) return null;

  if (!isLoaded) {
    return (
      <Skeleton
        flexShrink={0}
        aspectRatio={16 / 9}
        w={variant === "horizontal" ? "30%" : "full"}
        maxH={variant === "elevated" ? 84 : undefined}
        borderTopRadius={variant === "elevated" ? "md" : "none"}
      />
    );
  }

  const interactiveProps: ImageProps = {
    onClick: onClick ? undefined : onOpen,
    cursor: onClick ? "pointer" : "zoom-in",
    role: onClick ? "presentation" : undefined,
    _hover: { opacity: 0.7 }
  };

  const imageBaseProps: ImageProps = {
    src: imageSrc,
    alt: imageAlt,
    objectFit: "cover",
    borderRadius: "none"
  };

  const renderImage = () => {
    switch (variant) {
      case "horizontal":
        return (
          <Box flexShrink={0} w={{ base: "full", lg: "30%" }}>
            <StorybookImage
              minW="full"
              minH="full"
              {...imageBaseProps}
              {...interactiveProps}
            />
          </Box>
        );

      case "elevated":
        return (
          <StorybookImage
            h={84}
            minW="full"
            cursor="pointer"
            borderTopRadius="lg"
            {...imageBaseProps}
          />
        );

      case "vertical":
        return (
          <AspectRatio ratio={16 / 9}>
            <Box as="span" minW={100}>
              <StorybookImage
                minW="full"
                {...imageBaseProps}
                {...interactiveProps}
              />
            </Box>
          </AspectRatio>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderImage()}

      <Dialog.Root
        open={open}
        onOpenChange={handleOpenChange}
        placement="center"
        size={{ base: "full", lg: "xl" }}
      >
        <Portal>
          <Dialog.Backdrop bgColor="black" />
          <Dialog.Positioner>
            <Dialog.Content bgColor="black" color="white">
              <Dialog.Header
                {...flushDialogSlotPadding}
                p={3}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Header hideActionList />
                <Dialog.CloseTrigger position="static" />
              </Dialog.Header>

              <Dialog.Body {...flushDialogSlotPadding} p={4}>
                <StorybookImage
                  width="full"
                  maxHeight={456}
                  margin="0 auto"
                  objectFit="contain"
                  src={imageSrc}
                  alt={imageAlt}
                />
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
